/**
 * Feedback Submissions — what users actually said.
 *
 * Read-only by design: a submission is write-once, so there is no edit action.
 *
 * Feedback arrives here and goes NOWHERE until a person decides. Nothing is
 * routed automatically, so this list is the triage queue: someone holding
 * FEEDBACK_SUBMISSION.MANAGE opens a row and either marks it reviewed or
 * forwards it to an SME. Both live on the detail page rather than the row —
 * either is a judgement that needs the answers in front of you.
 *
 * Filter on NEW to find that work: because nothing routes itself, NEW means
 * exactly "has reached nobody". The moment it is forwarded it becomes IN_REVIEW,
 * so the queue empties as it is dealt with rather than needing a second filter.
 *
 * This screen is deliberately about EVERYONE's feedback, not yours. What has been
 * forwarded to you lives on the Reviews tab, where it comes with the verdict you
 * are being asked for. A "mine" filter here would be the third place the same
 * rows appeared, and the one where nothing could be done about them.
 *
 * Opening a row replaces the list with the full record. The three things an SME
 * needs — what was asked, what was answered, and what was happening around it —
 * only make sense together, and that is a page's worth of material rather than a
 * dialog's. The questionSnapshot is what lets it render an answer with the
 * wording the user actually saw, even after the definition has been edited since.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import SaasDataTable, { type SaasColumn } from '../../../components/common/saas-data-table';
import { ListCount } from '../../../components/common/list-count';
import { feedbackSubmissionService } from '../../../services/feedback-submission.service';
import type { FeedbackSubmission } from '../../../types/feedback-submission.types';
import { useToastApi } from '../../../hooks/use-toast-api';
import { useSubmissionStatuses } from '../use-feedback-event-value-sets';
import SubmissionDetail from '../submission-detail';
import { statusSeverity } from '../feedback-display';
import ProjectFilter from '../../../components/common/project-filter';
import ProjectCell from '../../../components/common/project-cell';
import { useAccessibleProjects } from '../../../hooks/useProjectScope';
import type { FeedbackScope } from '../feedback-scope';

/**
 * Which feedback this instance lists. The AI screen and the Feedback module are
 * the same screen over the same collection, pointed at different component types
 * — so the filter travels as a prop rather than the screen being copied.
 */
interface ScopedTabProps { scope?: FeedbackScope }

const SubmissionsTab = ({ scope }: ScopedTabProps = {}) => {
  const qc = useQueryClient();
  const { toast } = useToastApi();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  // '' = every project the caller may read, merged.
  const [appFilter, setAppFilter] = useState('');

  const { options: statusOptions } = useSubmissionStatuses();
  const { projects } = useAccessibleProjects();

  const listQ = useQuery({
    queryKey: ['feedback-submissions', { scope, page, pageSize, status, search, appFilter }],
    queryFn: () => feedbackSubmissionService.list({
      page, page_size: pageSize,
      scope,
      status: status || undefined,
      search: search || undefined,
      appId: appFilter || undefined,
    }),
    staleTime: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
    qc.invalidateQueries({ queryKey: ['feedback-review-inbox'] });
    qc.invalidateQueries({ queryKey: ['feedback-my-submissions'] });
    qc.invalidateQueries({ queryKey: ['feedback-assigned-to-me'] });
  };

  const columns: SaasColumn<FeedbackSubmission>[] = useMemo(() => [
    {
      field: 'feedbackKey', header: 'Feedback', sortable: true, hideable: false, style: { minWidth: 240 },
      body: (d) => (
        <div>
          <div className="font-mono text-xs font-semibold text-ink">
            {d.feedbackKey || '—'}
          </div>
          <div className="text-xs text-ink-muted">
            {d.module || '—'}
          </div>
        </div>
      ),
    },
    {
      field: 'component', header: 'Component', style: { minWidth: 190 },
      body: (d) => (
        <div>
          <Tag value={d.component?.componentType || '—'} severity="info" />
        </div>
      ),
    },
    {
      field: 'assignedSmeIds', header: 'SME', align: 'center', style: { width: 90 },
      body: (d) => {
        const n = d.assignedSmeIds?.length ?? 0;
        /* A number on every row, so the column reads as one thing you can scan
           down rather than a count on some rows and a sentence on others. Zero
           still carries the meaning the words used to — it is amber and says so
           on hover — but it lines up with the rest. */
        return (
          <span
            className={`text-sm tabular-nums ${n
              ? 'font-medium text-ink'
              : 'text-amber-600 dark:text-amber-400'}`}
            title={n
              ? `With ${n} SME${n > 1 ? 's' : ''}`
              : 'Nobody has been asked yet — mark it reviewed or forward it'}
          >
            {n}
          </span>
        );
      },
    },
    {
      field: 'status', header: 'Status', align: 'center', style: { width: 140 },
      body: (d) => <Tag value={d.status} severity={statusSeverity(d.status) as never} />,
    },
    {
      // Unconditional: with the list merged across projects by default, the row
      // is ambiguous without it. A caller who can reach one project still sees
      // which rows are that project's and which are shared.
      field: 'appId', header: 'Project', align: 'center', style: { width: 160 },
      body: (d) => <ProjectCell appId={d.appId} projects={projects} />,
    },
    {
      field: 'submittedAt', header: 'Submitted', sortable: true, style: { width: 170 },
      body: (d) => (
        <span className="text-xs text-ink-muted">
          {d.submittedAt ? new Date(d.submittedAt).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      // Close and Reopen are deliberately NOT here — they are decisions made from
      // the record, not from a status chip. Open is the only row action.
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 90 },
      body: (d) => (
        <div className="flex items-center justify-center gap-1">
          <Button aria-label="View" icon="pi pi-eye" text rounded tooltip="Open" onClick={() => setOpenId(d._id)} />
        </div>
      ),
    },
  ], [projects]);

  // The detail takes over the tab — no list behind it, no dialog on top of it.
  if (openId) {
    return (
      <>
        {toast}
        <SubmissionDetail
          submissionId={openId}
          onBack={() => setOpenId(null)}
          onChanged={invalidate}
        />
      </>
    );
  }

  return (
    <>
      {toast}
      <SaasDataTable
        tableId="feedback-submissions"
        columns={columns}
        data={listQ.data?.items ?? []}
        loading={listQ.isFetching}
        dataKey="_id"
        emptyMessage="No feedback submitted yet."
        totalRecords={listQ.data?.total ?? 0}
        first={(page - 1) * pageSize}
        rows={pageSize}
        onPage={(e: { first: number; rows: number }) => {
          setPageSize(e.rows);
          setPage(Math.floor(e.first / e.rows) + 1);
        }}
        onSort={() => undefined}
        onFilter={() => undefined}
        globalSearch={search}
        onGlobalSearchChange={setSearch}
        globalSearchPlaceholder="Search key, component id or trace id..."
        testId="FeedbackSubmissions"
        toolbarEnd={<ListCount total={listQ.data?.total} onRefresh={() => listQ.refetch()}
          loading={listQ.isFetching} testId="FeedbackSubmissions.Refresh" />}
        toolbarStart={<>
          <Dropdown value={status} options={[{ label: 'All statuses', value: '' }, ...statusOptions]}
            optionLabel="label" optionValue="value"
            onChange={(e: { value: string }) => { setStatus(e.value || ''); setPage(1); }}
            placeholder="Status" style={{ minWidth: 160 }} />
          {/* Back to page 1: offset 20 of one project is not offset 20 of another. */}
          <ProjectFilter value={appFilter} onChange={(appId) => { setAppFilter(appId); setPage(1); }}
            testId="FeedbackSubmissions.ProjectFilter" minWidth={170} />
        </>}
      />
    </>
  );
};

export default SubmissionsTab;
